import React, { FunctionComponent, useEffect } from "react";
import { connect } from "react-redux";
import { Paper, SvgIcon } from "@mui/material";
import { UserRole } from "avvir";

import "../../../css/nav_bar/nav_bar.css";
import * as Logo from "../../../resources/logo/hexagon-logo.svg";
import getPageName from "../../services/getters/location_metadata/get_page_name";
import getPageTitle from "../../services/getters/get_page_title";
import getProjectId from "../../services/getters/project_getters/get_project_id";
import getUserIsLoggedIn from "../../services/getters/user_getters/get_user_is_logged_in";
import getUserRole from "../../services/getters/user_getters/get_user_role";
import Link from "../../vendor/redux-first-router-link";
import NavBarPagesDropdown from "./nav_bar_pages_dropdown";
import NavPageControls from "./nav_bar_page_controls";
import PageNames, { PROJECT_PORTFOLIO } from "../../models/domain/enums/page_names";
import Tooltipable from "../shared/tooltipable";
import toProjectPortfolioPage from "../../events/routing/to_project_portfolio_page";
import toSuperadminOrganizations from "../../events/routing/superadmin/to_superadmin_organizations";
import { useFlags } from "../../services/utilities/launch_darkly_helper";

const LogoIcon = <SvgIcon className="NavBar-logo" component={Logo.ReactComponent} viewBox="0 0 240 240"/>;

export type Props = ReturnType<typeof mapStateToProps>;

export const NavBar: FunctionComponent<Props> = (props) => {
  const {
    userIsLoggedIn,
    projectId,
    pageName,
    pageTitle,
    userRole,
  } = props;

  const { projectPortfolioReskin } = useFlags();
  // The reskinned portfolio page (Figma: node 3455-117639) drops the page title,
  // the divider after the logo, and the navbar's bottom shadow. Scoped to that
  // page + flag so every other page keeps the existing navbar untouched.
  const isReskinnedPortfolioNavBar = pageName === PROJECT_PORTFOLIO && projectPortfolioReskin;

  const isSuperadmin = userRole === UserRole.SUPERADMIN;
  const logoLink = isSuperadmin ? toSuperadminOrganizations() : toProjectPortfolioPage();
  const logoTooltip = isSuperadmin ? "Go to Organizations" : "Go to your project portfolio";

  const logo = LogoIcon;
  const className = `NavBar hexagon-construction-analysis-logo`;
  const logoClassName = `NavBar-logo${isReskinnedPortfolioNavBar ? " NavBar-logo--noDivider" : ""}`;

  if (userIsLoggedIn) {
    return <Paper
      className={className}
      square
      elevation={isReskinnedPortfolioNavBar ? 0 : 1}
    >
      <Tooltipable title={logoTooltip}>
        <Link className={logoClassName} to={logoLink} aria-label="Hexagon Logo">
          {logo}
        </Link>
      </Tooltipable>
      {!isReskinnedPortfolioNavBar && (projectId ? <>
        <span className="NavBar-pageName" aria-label="Project Name">{pageTitle}</span>
        {pageName !== PageNames.SUPPORT_CENTER && <NavBarPagesDropdown/>}
      </> : <span className="NavBar-pageName" aria-label="Page Title">{pageTitle}</span>)}
      <NavPageControls/>
    </Paper>;
  } else {
    return <Paper
      className={className}
      square
    >
      <span className="NavBar-logo">
        {logo}
      </span>
    </Paper>;
  }
};

const mapStateToProps = (state, props) => ({
  userIsLoggedIn: getUserIsLoggedIn(state, props),
  projectId: getProjectId(state, props),
  pageName: getPageName(state, props),
  pageTitle: getPageTitle(state, props),
  userRole: getUserRole(state, props),
});

export default connect(mapStateToProps)(NavBar);
